"use client"

import { ReactNode, useCallback, useEffect } from "react"
import { usePathname } from "next/navigation"
import {
  getUser,
  logout,
  setClearBasket,
  setUpdatedUser,
  useGetMeQuery,
} from "@/redux/features"
import { useAppDispatch, useAppSelector } from "@/redux/hooks"
import { debug, decodeToken, getTokenWithoutRedirect } from "@/utils"
import { useRouter } from "nextjs-toploader/app"

import { logOut } from "@/lib/action"
import { notifyError } from "@/components/toast"

interface UserProviderProps {
  children: ReactNode
}

export function UserProvider({ children }: UserProviderProps) {
  const dispatch = useAppDispatch()
  const pathname = usePathname()
  const router = useRouter()

  const token = useAppSelector((state) => state.auth.token)
  const updatedUser = useAppSelector(getUser)
  const updatedAtLocal = updatedUser?.updatedAt

  const { data, isLoading } = useGetMeQuery(undefined, {
    skip: !token,
  })
  const user = data?.data
  const updatedAtServer = user?.updatedAt

  const handleSessionExpiry = useCallback(async () => {
    dispatch(logout())
    dispatch(setClearBasket())

    try {
      await logOut()
    } catch (error) {
      debug("Error logging out", { level: "error", data: error })
    }

    notifyError({
      title: "Session Expired",
      message: "Your session has been expired. Please login again!",
    })

    if (pathname !== "/") {
      router.push("/")
    }
  }, [dispatch, pathname, router])

  useEffect(() => {
    const checkSession = async () => {
      try {
        if (token) {
          const decoded = decodeToken(token)
          const exp = decoded?.exp * 1000
          const now = Date.now()

          if (exp < now) {
            handleSessionExpiry()
            return
          }
        }

        const cookieToken = await getTokenWithoutRedirect()
        if (cookieToken && !updatedUser && !token) {
          handleSessionExpiry()
        }
      } catch (error) {
        debug("Error during session check", { level: "error", data: error })
      }
    }

    checkSession()
  }, [token, user, updatedUser, handleSessionExpiry])

  useEffect(() => {
    if (!isLoading && user && updatedAtLocal !== updatedAtServer) {
      dispatch(setUpdatedUser(user))
    }
  }, [dispatch, isLoading, updatedAtLocal, updatedAtServer, user])

  return <>{children}</>
}
